/**
 * Foto bij een persona (public/assets/personas), gekozen op geslacht en leeftijd.
 * Gedeeld door Fase 3 en het dossier, zodat beide dezelfde foto tonen.
 */

export type Gender = 'male' | 'female' | '';

// Leeftijd = hoe oud iemand op de foto oogt, niet het getal in de bestandsnaam.
// Waar die sterk verschillen (Male_55, Male_63, Female_62) volgt de lijst de foto.
const AVATAR_LIJST: [string, Gender, number][] = [
  ['Female_21', 'female', 22], ['Female_23', 'female', 25], ['Female_26', 'female', 28], ['Female_30', 'female', 31],
  ['Female_35', 'female', 35], ['Female_36', 'female', 36], ['Female_38', 'female', 38], ['Female_39', 'female', 39],
  ['Female_40', 'female', 41], ['Female_45', 'female', 44], ['Female_46', 'female', 45], ['Female_47', 'female', 46],
  ['Female_49', 'female', 50], ['Female_51', 'female', 54], ['Female_55', 'female', 58], ['Female_66', 'female', 61],
  ['Female_75', 'female', 63], ['Female_62', 'female', 70],
  ['Male_23', 'male', 25], ['Male_25', 'male', 27], ['Male_28', 'male', 30], ['Male_30', 'male', 32],
  ['Male_35', 'male', 35], ['Male_36', 'male', 36], ['Male_37', 'male', 38], ['Male_42', 'male', 42],
  ['Male_46', 'male', 44], ['Male_49', 'male', 48], ['Male_54', 'male', 53], ['Male_60', 'male', 58],
  ['Male_65', 'male', 61], ['Male_55', 'male', 64], ['Male_75', 'male', 66], ['Male_76', 'male', 69],
  ['Male_77', 'male', 70], ['Male_71', 'male', 73], ['Male_63', 'male', 75],
];
const AVATARS = AVATAR_LIJST.map(([bestand, gender, age]) => ({ gender, age, src: `/assets/personas/${bestand}.png` }));

// Veelvoorkomende voornamen in Nederland (alle generaties en herkomsten). Namen die voor
// beide geslachten gangbaar zijn (Sam, Kim, Robin, Charlie, Jip, Luca…) staan er bewust niet in.
const VROUWEN = new Set(`
anja anne annemarie annelies annette astrid anna anouk ans ank bea bep betty bianca brigitte carla carolien caroline
cathy chantal charlotte claudia corrie corry dewi diana dieuwke dina dorien els elly emma esther eva evelien fatima
fatma femke floor fleur gerda greet hanneke hannah hanna henny hilde ilse inge ingrid irene iris jacqueline janneke
jantine jasmijn jeanette jolanda joke josé joyce judith julia karin kirsten laura lieke linda lisa lisanne lotte loes
lucia manon margreet maria marieke marja marjolein marleen martine mieke mirjam monique naima nadia nicole nina noa
nora noor paula petra priya renate rianne rita roos rosa sandra sanne saskia selma shanti silvia sophie stephanie
susan suzanne tamara tineke tanja truus ellen wilma yvonne zeynep aylin amira hatice samira khadija yasmina esra
sevda leyla ayse emine meryem tessa wendy maaike jessica jorien marloes karlijn evi fenna lina mila yara sara
`.trim().split(/\s+/));
const MANNEN = new Set(`
ahmed ali arjan bart bas ben bert bram casper cees cor daan david dennis dick dirk edwin erik ernst frank frans freek
gerard gert gijs guus hans harm hendrik henk herman huib jaap jacob jan jasper jeroen jesse johan jos joost jurgen
kees kevin klaas koen lars leo lucas luuk maarten marcel marco mark martijn martin matthijs max michiel mohamed mohammed
mustafa niels nick noah olaf patrick paul peter pieter piet ramon remco rene richard rick rik rob robert roel ronald
ruud sander sem siem stefan sven teun theo thijs thomas tim tom ton twan vincent wesley willem wim youssef yusuf
hamza mehmet murat emre omar karim rachid hassan ibrahim ivo harry harrie jorim frits gerrit hugo job jelle joep
floris wouter reinier otto hidde ties milan finn stijn rutger dries henri alex
`.trim().split(/\s+/));

export function normGender(g?: string): Gender {
  const s = (g || '').toLowerCase();
  if (/vrouw|female|^f$|^v$/.test(s)) return 'female';
  if (/man|male|^m$/.test(s)) return 'male';
  return '';
}

export function genderNL(g?: string): '' | 'vrouw' | 'man' {
  const n = normGender(g);
  return n === 'female' ? 'vrouw' : n === 'male' ? 'man' : '';
}

/** Geslacht afleiden uit de voornaam in de personanaam ("Ondernemer Kees (45)" → man). */
export function geslachtUitNaam(naam?: string): Gender {
  const woorden = String(naam || '').replace(/\([^)]*\)/g, ' ').split(/[^A-Za-zÀ-ÿ'-]+/).filter(Boolean);
  for (const w of woorden) {
    const k = w.toLowerCase();
    if (VROUWEN.has(k)) return 'female';
    if (MANNEN.has(k)) return 'male';
  }
  return '';
}

export function ageFromName(naam?: string): number | undefined {
  const m = (naam || '').match(/\((\d{1,3})\)/);
  return m ? parseInt(m[1], 10) : undefined;
}

function pickAvatar(gender: Gender, age?: number): string {
  const pool = AVATARS.filter((a) => a.gender === gender);
  const target = typeof age === 'number' && !isNaN(age) ? age : 45;
  let best = pool[0];
  for (const a of pool) if (Math.abs(a.age - target) < Math.abs(best.age - target)) best = a;
  return best.src;
}

/**
 * Best passende foto voor een persona. Geslacht uit het veld, anders uit de voornaam; nooit uit
 * een eerder gekozen foto (daardoor bleef een verkeerde foto zichzelf in stand houden).
 * Geen geslacht te bepalen → '' (dan tonen we initialen in plaats van een foto die misschien niet klopt).
 */
export function deriveAvatar(p: { naam?: string; geslacht?: string; leeftijd?: string }): string {
  const g = normGender(p.geslacht) || geslachtUitNaam(p.naam);
  if (!g) return '';
  const parsed = p.leeftijd && !isNaN(parseInt(p.leeftijd, 10)) ? parseInt(p.leeftijd, 10) : undefined;
  return pickAvatar(g, parsed ?? ageFromName(p.naam));
}
